import { useState } from "react";

import { useTranslation } from "next-i18next";

import {
  useAgentGrants,
  useRevokeAgentGrant,
} from "@/Apollo/hooks/agent-oauth/useAgentOAuth";
import {
  AGENT_OAUTH_COPY,
  AGENT_SCOPE_COPY,
} from "@/features/agent-oauth/agent-oauth.static";
import addToast from "@/toast/addToast";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";

/**
 * WHO-6316: the AI assistants this member connected, with one-click
 * disconnect (agent actions standard A1.4). Renders nothing until the member
 * has connected one, so it stays invisible while the feature is in pilot.
 */
export const ConnectedAssistants = () => {
  const { t, i18n } = useTranslation();
  const { data, refetch } = useAgentGrants({ fetchPolicy: "network-only" });
  const [revoke] = useRevokeAgentGrant();
  // One row at a time: only the assistant being disconnected shows a spinner.
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const grants = data?.agentGrants ?? [];

  if (grants.length === 0) {
    return null;
  }

  const disconnect = async (id: string, client: string) => {
    setRevokingId(id);

    try {
      await revoke({ variables: { id } });
      addToast(
        t(
          AGENT_OAUTH_COPY.disconnected.labelKey,
          AGENT_OAUTH_COPY.disconnected.defaultLabel,
          {
            client,
          }
        ),
        { appearance: "success" }
      );
      await refetch();
    } catch {
      addToast(
        t(AGENT_OAUTH_COPY.error.labelKey, AGENT_OAUTH_COPY.error.defaultLabel),
        {
          appearance: "error",
        }
      );
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className="mt-10" aria-labelledby="connected-assistants-title">
      <Heading as="h2" size="h4" id="connected-assistants-title">
        {t(
          AGENT_OAUTH_COPY.connectedTitle.labelKey,
          AGENT_OAUTH_COPY.connectedTitle.defaultLabel
        )}
      </Heading>
      <p className="mt-2">
        {t(
          AGENT_OAUTH_COPY.connectedIntro.labelKey,
          AGENT_OAUTH_COPY.connectedIntro.defaultLabel
        )}
      </p>
      <ul className="mt-4 divide-y divide-grey-200">
        {grants.map(grant => (
          <li
            key={grant.id}
            className="flex items-start justify-between gap-4 py-4"
          >
            <div>
              <p className="font-bold">{grant.clientName}</p>
              <p className="text-sm">
                {grant.scopes
                  .map(scope => {
                    const scopeCopy = AGENT_SCOPE_COPY[scope];

                    return scopeCopy
                      ? t(scopeCopy.labelKey, scopeCopy.defaultLabel)
                      : scope;
                  })
                  .join(". ")}
              </p>
              <p className="mt-1 text-sm text-grey-500">
                {grant.lastUsedAt
                  ? t(
                      AGENT_OAUTH_COPY.lastUsed.labelKey,
                      AGENT_OAUTH_COPY.lastUsed.defaultLabel,
                      {
                        date: new Date(grant.lastUsedAt).toLocaleDateString(
                          i18n.language
                        ),
                      }
                    )
                  : t(
                      AGENT_OAUTH_COPY.neverUsed.labelKey,
                      AGENT_OAUTH_COPY.neverUsed.defaultLabel
                    )}
              </p>
            </div>
            <Button
              loading={revokingId === grant.id}
              disabled={revokingId !== null}
              onClick={() => disconnect(grant.id, grant.clientName)}
            >
              {t(
                AGENT_OAUTH_COPY.disconnect.labelKey,
                AGENT_OAUTH_COPY.disconnect.defaultLabel
              )}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
};
